import { useRef, useState } from 'react';
import { IconPlus, IconStar, IconStarFilled, IconTrash } from '@tabler/icons-react';
import { ActionIcon } from '../../atoms/ActionIcon';
import { Box } from '../../atoms/Box';
import { Button } from '../../atoms/Button';
import { Group } from '../../atoms/Group';
import { Stack } from '../../atoms/Stack';
import { Text } from '../../atoms/Text';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';
import { ProductImageAvatar } from './ProductImageAvatar';

export type ManagedProductImage = {
  fileId: string;
  url: string;
  isMain?: boolean;
};

export interface ProductImageManagerProps extends CoreProps {
  images: ManagedProductImage[];
  onChange: (images: ManagedProductImage[]) => void;
  onUpload: (file: File) => Promise<{ id: string; url: string }>;
  max?: number;
  label?: string;
  setMainLabel?: string;
  addLabel?: string;
  disabled?: boolean;
  onImageClick?: (image: ManagedProductImage, index: number) => void;
}

export function ProductImageManager({
  images,
  onChange,
  onUpload,
  max = 8,
  label = 'Images',
  setMainLabel = 'Set as main',
  addLabel = 'Add images',
  disabled = false,
  onImageClick,
  testId,
  view = 'all',
}: ProductImageManagerProps) {
  const shouldRender = useShouldRender(view);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  if (!shouldRender) return null;

  const canAdd = !disabled && images.length < max;

  async function handleFiles(fileList: FileList | null) {
    if (!fileList?.length || disabled || uploading) return;
    const remaining = max - images.length;
    const files = Array.from(fileList).slice(0, remaining);
    if (!files.length) return;
    setUploading(true);
    try {
      const next = [...images];
      for (const file of files) {
        const uploaded = await onUpload(file);
        if (next.some((img) => img.fileId === uploaded.id)) continue;
        next.push({
          fileId: uploaded.id,
          url: uploaded.url,
          isMain: next.length === 0,
        });
      }
      if (!next.some((img) => img.isMain) && next[0]) {
        next[0] = { ...next[0], isMain: true };
      }
      onChange(next);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function setMain(fileId: string) {
    onChange(images.map((img) => ({ ...img, isMain: img.fileId === fileId })));
  }

  function remove(fileId: string) {
    const next = images.filter((img) => img.fileId !== fileId);
    if (next.length && !next.some((img) => img.isMain)) {
      next[0] = { ...next[0]!, isMain: true };
    }
    onChange(next);
  }

  return (
    <Stack gap="sm" data-testid={testId}>
      <Text size="sm" fw={600}>
        {label}
      </Text>
      <Group gap="sm" align="flex-start">
        {images.map((img) => (
          <Box key={img.fileId} style={{ position: 'relative', width: 88 }}>
            <ProductImageAvatar
              src={img.url}
              name={label}
              size={88}
              radius={12}
              onClick={
                onImageClick
                  ? () => onImageClick(img, images.findIndex((i) => i.fileId === img.fileId))
                  : undefined
              }
            />
            <Group gap={4} justify="center" mt={6} wrap="nowrap">
              <ActionIcon
                size="sm"
                variant={img.isMain ? 'filled' : 'default'}
                color={img.isMain ? 'yellow' : 'gray'}
                aria-label={setMainLabel}
                disabled={disabled}
                onClick={() => setMain(img.fileId)}
              >
                {img.isMain ? <IconStarFilled size={14} /> : <IconStar size={14} />}
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="default"
                color="red"
                aria-label="Remove"
                disabled={disabled}
                onClick={() => remove(img.fileId)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
            {img.isMain ? (
              <Text size="xs" ta="center" c="dimmed" mt={2}>
                {setMainLabel}
              </Text>
            ) : null}
          </Box>
        ))}
        {canAdd ? (
          <Button
            variant="light"
            loading={uploading}
            leftSection={<IconPlus size={16} />}
            onClick={() => inputRef.current?.click()}
            style={{ height: 88, minWidth: 88 }}
          >
            {addLabel}
          </Button>
        ) : null}
      </Group>
      <Text size="xs" c="dimmed">
        {images.length}/{max}
      </Text>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => void handleFiles(e.target.files)}
      />
    </Stack>
  );
}
